import { useEffect, useMemo, useRef, useState } from "react";
/* O vocabulario de falha de carga: frase em portugues, sem o token, e com a
   FALHA decidida antes do VAZIO. Ver `lib/falha-de-carga.ts`. */
import {
  falhaDe,
  fraseDeFalhaDeCarga,
  type FalhaDeCarga,
} from "../lib/falha-de-carga.js";
import { useNavigate } from "react-router-dom";
import { FLEET_STALE_AFTER_SECONDS, type RespostaDeFrota } from "@nxvolt/shared";
import { Skeleton } from "../components/Skeleton.js";
import { VehicleDetailModal } from "../components/VehicleDetailModal.js";
import {
  CriticalBatteryAlert,
  type CriticalVehicleInput,
} from "../components/CriticalBatteryAlert.js";
import { fmtAge } from "../components/substation/bands.js";
import {
  celulaDeCampo,
  compararPorIdade,
  configuracaoDeFrota,
  contagemDaFrota,
  contagemDeMovimento,
  contagemDoCampo,
  estadoDeMovimento,
  FRASE_DO_MOVIMENTO,
  linhasDescartadas,
  maximoDoCampo,
  mediaDoCampo,
  minimoDoCampo,
  notaDeCamposOcultos,
  numeroUtilizavel,
  ressalvaDeTruncamento,
  campoOculto,
  estadoDoCampo,
  posicaoDoVeiculo,
  projetarFrota,
  semSinalDaFrota,
  valorDeParcela,
  fraseDeCarimboFuturoNaTabela,
  fraseDeLinhasDescartadas,
  fraseDeOnibusVisiveis,
  fraseDeAtualizacaoParada,
  contradicoesDeLeitura,
  fraseDeContradicaoDeLeitura,
  rotuloDoVeiculo,
  textoDaIdade,
  valorUtilizavel,
  type AgregadoDeFrota,
  type CampoDeVeiculo,
  type EstadoDeMovimento,
  type VeiculoDeFrota,
} from "../components/fleet/aggregates.js";
import {
  AvisoDeInconsistencia,
  BadgeDeMovimento,
  CampoCelula,
  KpiDeFrota,
  NotaDeFonte,
} from "../components/fleet/FleetCells.js";

/**
 * Cache de endereços geocodificados por bucket lat/lon (4 casas ≈ 11m).
 * Um Map compartilhado entre todas as linhas da tabela evita duplicar
 * requisições quando dois ônibus estão a menos de ~11m um do outro.
 */
const geocodeCache = new Map<string, string>();

/**
 * Fila serializada de geocode. Sem isso, uma tela com 60 veículos dispara
 * 60 fetches paralelos e satura o rate-limit interno (429/503). Um único
 * worker consome a fila com backoff entre chamadas.
 */
const geocodeQueue: Array<() => Promise<void>> = [];
let geocodeWorkerActive = false;

async function geocodeWorker(): Promise<void> {
  if (geocodeWorkerActive) return;
  geocodeWorkerActive = true;
  try {
    while (geocodeQueue.length > 0) {
      const job = geocodeQueue.shift()!;
      try {
        await job();
      } catch {
        /* ignora — job cuida do próprio erro */
      }
      // Nominatim aceita ~1 req/s; nosso servidor tem cache mas primeira carga
      // atinge o upstream direto. 500ms deixa margem confortável.
      await new Promise((res) => setTimeout(res, 500));
    }
  } finally {
    geocodeWorkerActive = false;
  }
}

function enqueueGeocode(job: () => Promise<void>): void {
  geocodeQueue.push(job);
  void geocodeWorker();
}

function gpsBucket(lat: number, lon: number): string {
  return `${lat.toFixed(4)},${lon.toFixed(4)}`;
}

/**
 * Célula GPS: mostra endereço (rua + número) via /api/geocode/reverse,
 * fallback pra coordenadas. Clique abre /app/mapa-frota?focus=<vin>.
 *
 * Recebe a POSIÇÃO JÁ VALIDADA por `posicaoDoVeiculo` — que recusa campo não
 * fornecido pela fonte, carimbo à frente do relógio, coordenada fora de domínio
 * e o `(0, 0)` do GPS sem fix. A célula não repete nenhuma dessas perguntas.
 */
function GpsCell({
  posicao,
  frase,
  vin,
  onNavigate,
}: {
  posicao: [number, number] | null;
  frase: string;
  vin: string;
  onNavigate: (vin: string) => void;
}) {
  const [address, setAddress] = useState<string | null>(null);

  useEffect(() => {
    if (posicao === null) return;
    const [lat, lon] = posicao;
    const bucket = gpsBucket(lat, lon);
    const cached = geocodeCache.get(bucket);
    if (cached) {
      setAddress(cached);
      return;
    }
    let alive = true;
    enqueueGeocode(async () => {
      if (!alive) return;
      try {
        const r = await fetch(
          `/api/geocode/reverse?lat=${lat}&lon=${lon}`,
          { credentials: "include" },
        );
        if (!r.ok) return;
        const body = (await r.json()) as { label?: string };
        if (!body.label) return;
        geocodeCache.set(bucket, body.label);
        if (alive) setAddress(body.label);
      } catch {
        /* sem endereço — a célula cai nas coordenadas */
      }
    });
    return () => {
      alive = false;
    };
  }, [posicao]);

  if (posicao === null) {
    return (
      <>
        <span aria-hidden="true" style={{ color: "var(--nx-mute)" }}>
          —
        </span>
        <span className="nx-sr-only">{frase}</span>
      </>
    );
  }

  const [lat, lon] = posicao;
  return (
    <button
      type="button"
      onClick={() => onNavigate(vin)}
      title={`${frase} Abrir no mapa.`}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        font: "inherit",
        color: "var(--nx-link)",
        cursor: "pointer",
        textAlign: "left",
        textDecoration: "underline",
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {address ?? `${lat.toFixed(4)}, ${lon.toFixed(4)}`}
    </button>
  );
}

/**
 * Tela "Dados dos Ônibus" — snapshot de cada veículo VISÍVEL para a fonte de
 * telemetria ativa, lido de `public.fleet_snapshot`.
 *
 * ---------------------------------------------------------------------------
 * O QUE MUDOU NA AUDITORIA DE 29/09/2026, e cada item é defeito VERIFICADO:
 *
 *  [denominador]      `stats` vinha de `COUNT(*) FROM vehicle_telemetry` e as
 *                     linhas de `signalstore_vehicles LEFT JOIN` — DUAS
 *                     RELAÇÕES CONTADAS COMO UMA. A consulta de estatísticas
 *                     foi APAGADA: os KPIs derivam do mesmo array que a tabela
 *                     renderiza, e duas consultas que viraram uma não podem
 *                     divergir.
 *  [M de N]           `soc_medio`/`min`/`max` saíam sem denominador, com SOC em
 *                     14 de 16. Agora cada um declara "14 de 16", e o
 *                     denominador é a TOPOLOGIA visível.
 *  [impossibilidade]  As colunas que a fonte é incapaz de preencher SOMEM, com
 *                     nota nomeando a fonte. Cinco travessões lado a lado
 *                     ensinam a ignorar travessão, e aí o travessão legítimo
 *                     dos 2 sem SOC morre junto.
 *  [ausência≠vencim.] `stale = !v.is_active` dava `true` para veículo sem linha
 *                     de telemetria (`!null`), e a tooltip afirmava "dados são
 *                     o último snapshot recebido" sobre quem NUNCA reportou.
 *                     `is_active` saiu do contrato; entra `ageSeconds`
 *                     assinado, classificado por `estadoDeMovimento`.
 *  [carimbo futuro]   Nada perguntava isso. Erro de fuso no `AT TIME ZONE 'UTC'`
 *                     manual do `sync.sql` = 10.800 s de carimbo futuro em 16
 *                     veículos, e antes sairia como "há 0 s".
 *  [zero]             `charging_power_kw` nunca imprime `0,0 kW` por ausência.
 *  [sanity]           "Carregando" contava veículo sem sinal há 4 semanas.
 * ---------------------------------------------------------------------------
 */

/** Chave de ordenação. As de campo são derivadas do próprio `CampoDeVeiculo`. */
type SortKey = "prefix" | "chassi" | "estado" | "gps" | "updated" | CampoDeVeiculo;

/**
 * As colunas da tabela, DECLARATIVAS.
 *
 * Existe assim porque ocultação por impossibilidade, `colSpan` do estado vazio
 * e ordenação precisam sair do MESMO lugar. Antes eram três listas paralelas
 * (o array de cabeçalhos, o `switch` do comparador e o `colSpan={10}` cravado),
 * e três listas paralelas divergem — o `colSpan` já estava errado em relação ao
 * número de colunas na primeira vez que alguém acrescentasse uma.
 */
type Coluna =
  | {
      tipo: "campo";
      campo: CampoDeVeiculo;
      label: string;
      num: boolean;
      formatar: (valor: number | boolean | string) => string;
    }
  | { tipo: "identidade" | "estado" | "gps" | "atualizado"; key: SortKey; label: string };

const num0 = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
const num1 = (v: number) =>
  v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const COLUNAS: readonly Coluna[] = [
  { tipo: "identidade", key: "prefix", label: "Prefixo" },
  { tipo: "identidade", key: "chassi", label: "Chassi" },
  { tipo: "estado", key: "estado", label: "Estado" },
  {
    tipo: "campo",
    campo: "soc_pct",
    label: "SOC",
    num: true,
    formatar: (v) => `${num1(Number(v))}%`,
  },
  {
    tipo: "campo",
    campo: "range_km",
    label: "Autonomia",
    num: true,
    formatar: (v) => `${num0(Number(v))} km`,
  },
  {
    tipo: "campo",
    campo: "speed_kmh",
    label: "Vel.",
    num: true,
    formatar: (v) => `${num0(Number(v))} km/h`,
  },
  {
    tipo: "campo",
    campo: "odometer_km",
    label: "Odôm.",
    num: true,
    formatar: (v) => `${num0(Number(v))} km`,
  },
  {
    tipo: "campo",
    campo: "cell_temp_avg",
    label: "Temp. cél.",
    num: true,
    formatar: (v) => `${num1(Number(v))} °C`,
  },
  {
    tipo: "campo",
    campo: "charging_power_kw",
    label: "Pot. carga",
    num: true,
    formatar: (v) => `${num1(Number(v))} kW`,
  },
  { tipo: "gps", key: "gps", label: "GPS" },
  { tipo: "atualizado", key: "updated", label: "Atualizado" },
];

/** Ordem lógica dos estados na ordenação por "Estado". */
const ORDEM_DO_MOVIMENTO: Record<EstadoDeMovimento, number> = {
  carregando: 0,
  rodando: 1,
  parado: 2,
  indeterminado: 3,
  "sem-sinal": 4,
  "carimbo-futuro": 5,
  "sem-leitura": 6,
};

function chaveDaColuna(coluna: Coluna): SortKey {
  return coluna.tipo === "campo" ? coluna.campo : coluna.key;
}

/** O KPI de agregado: valor + a frase que declara M de N. */
function KpiAgregado({
  label,
  agregado,
  formatar,
  tone,
}: {
  label: string;
  agregado: AgregadoDeFrota;
  formatar: (v: number) => string;
  tone: "navy" | "success" | "orange" | "cyan";
}) {
  /* `(inclui último)` só aparece quando há valor E ele foi composto com leitura
     vencida. Sobre um travessão a ressalva não ressalvaria nada. */
  const degradado = agregado.valor !== null && agregado.vencidosUsados > 0;
  return (
    <KpiDeFrota
      label={label}
      valor={agregado.valor === null ? null : formatar(agregado.valor)}
      hint={agregado.frase}
      tone={tone}
      degradado={degradado}
      {...(degradado ? { sufixo: "(inclui último)" } : {})}
    />
  );
}

export function TelemetriaFrotaPage() {
  const [data, setData] = useState<RespostaDeFrota | null>(null);
  const [error, setError] = useState<FalhaDeCarga | null>(null);
  const [now, setNow] = useState(new Date());
  /**
   * A FALHA DE ATUALIZACAO, contada.
   *
   * `error` sozinho nao servia: ele so aparece na tela quando `!data` (a carga
   * inicial). Depois do primeiro sucesso, TODA falha de refresh era silenciosa —
   * medido com todo `fetch` rejeitando por 35 s: o carimbo congelava, o que esta
   * certo, e nao havia aviso nenhum. A entrega tem vocabulario para "a FONTE
   * parou" e nao tinha nenhum para "o PORTAL parou de buscar".
   *
   * Duas coisas sao necessarias para a frase ser verdadeira: quantas tentativas
   * falharam SEGUIDAS e quando foi o ultimo sucesso. `tentativas` zera no proximo
   * sucesso — uma falha isolada em 15 s de polling nao merece banner.
   */
  const [falhaDeAtualizacao, setFalhaDeAtualizacao] = useState<{
    tentativas: number;
    ultimoSucesso: number | null;
    /**
     * QUANDO A ULTIMA TENTATIVA FALHOU, e este campo e o conserto de um defeito
     * que a validacao em navegador encontrou: a idade do ultimo sucesso era
     * calculada contra `now`, e `now` so avanca EM SUCESSO. O banner dizia
     * "a ultima resposta bem-sucedida tem 0 s" com 35 s de falhas atras.
     *
     * Um aviso que existe para dizer que o dado esta envelhecendo, afirmando
     * que ele tem zero segundo, e a mesma familia de defeito que a fase
     * inteira persegue — dentro do aviso escrito contra ela.
     *
     * A idade agora e medida no instante da FALHA, e reavalia a cada poll.
     */
    falhouEm: number;
  } | null>(null);
  // Filtro client-side por prefixo (ou chassi). Case-insensitive, substring.
  const [filter, setFilter] = useState("");
  // VIN do veículo aberto no modal de detalhes; null = modal fechado.
  const [selectedVin, setSelectedVin] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
    }
  };
  const sortIndicator = (key: SortKey): string => {
    if (sortKey !== key) return " ⇅";
    return sortDir === "asc" ? " ↑" : " ↓";
  };
  // Auto-refresh (30s) — off por padrão pra não gastar tráfego móvel.
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => {
    try {
      return localStorage.getItem("nx.telemetria.autorefresh") === "1";
    } catch {
      return false;
    }
  });
  const [criticalTarget, setCriticalTarget] = useState<CriticalVehicleInput | null>(null);
  const navigate = useNavigate();
  const criticalDispatchedRef = useRef<Set<string>>(new Set());
  /** Instante do ultimo `fetch` bem-sucedido. `null` = nunca houve um. */
  const ultimoSucessoRef = useRef<number | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem("nx.telemetria.autorefresh", autoRefresh ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [autoRefresh]);

  /**
   * Filtro por estado. `offline` virou `sem-sinal` e ganhou dois vizinhos que
   * antes não existiam: `sem-leitura` (nunca reportou) e `indeterminado` (a
   * leitura é corrente e não decide o estado). Sem eles, o operador que
   * filtrasse "Offline" não encontrava o veículo que nunca reportou — e ele é o
   * que mais precisa de atenção.
   */
  const [statusFilter, setStatusFilter] = useState<"" | EstadoDeMovimento>("");

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const r = await fetch("/api/telemetria-frota", {
          credentials: "include",
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = (await r.json()) as RespostaDeFrota;
        if (controller.signal.aborted) return;
        setData(json);
        setError(null);
        setNow(new Date());
        /* Sucesso ZERA a contagem: o banner some sozinho quando a busca volta. */
        ultimoSucessoRef.current = Date.now();
        setFalhaDeAtualizacao(null);
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        setError(falhaDe(e));
        /* `Date.now()` aqui e o relogio do NAVEGADOR e e legitimo: ele mede a
           idade da RESPOSTA DO PORTAL, nao o frescor de uma leitura de veiculo.
           O que a guarda de texto proibe — e com razao — e derivar frescor de
           telemetria dele; esta idade e a de um evento que aconteceu nesta aba. */
        setFalhaDeAtualizacao((antes) => ({
          tentativas: (antes?.tentativas ?? 0) + 1,
          ultimoSucesso: ultimoSucessoRef.current,
          falhouEm: Date.now(),
        }));
      }
    };

    void load();
    const interval = autoRefresh ? 30_000 : 15_000;
    const t = setInterval(() => {
      if (!document.hidden) void load();
    }, interval);
    const onVis = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      controller.abort();
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [autoRefresh]);

  /**
   * A PROJEÇÃO, e é aqui que `ageSeconds: null` vira `latest: null`.
   *
   * `configuration.staleAfterSeconds` vem do envelope; `FLEET_STALE_AFTER_SECONDS`
   * é só o fallback para o envelope antigo. O limiar NÃO é literal nesta tela —
   * havia quatro limiares diferentes classificando a mesma frota.
   */
  const cfg = useMemo(
    () => configuracaoDeFrota(data?.configuration?.staleAfterSeconds ?? FLEET_STALE_AFTER_SECONDS),
    [data?.configuration?.staleAfterSeconds],
  );
  const veiculos = useMemo(() => projetarFrota(data?.vehicles ?? []), [data?.vehicles]);
  const descartadas = useMemo(() => linhasDescartadas(data?.vehicles ?? []), [data?.vehicles]);

  /**
   * Detector de bateria crítica (SOC ≤ 5%).
   *
   * `valorUtilizavel` no lugar de `v.soc_pct`: só dispara com SOC de LEITURA
   * CORRENTE. Antes o gate era `if (!v.is_active) continue`, que deixava passar
   * o veículo que nunca reportou (`!null === true`) — e despachar reforço para
   * um ônibus com SOC de quatro semanas atrás é o pior falso positivo que esta
   * tela pode produzir.
   */
  useEffect(() => {
    if (criticalTarget) return;
    const agora = Date.now();
    for (const v of veiculos) {
      if (estadoDoCampo(v, "soc_pct", cfg) !== "corrente") continue;
      const soc = numeroUtilizavel(v, "soc_pct", cfg);
      if (soc === null || soc > 5) continue;
      if (criticalDispatchedRef.current.has(v.chassiVin)) continue;
      let snoozed = false;
      try {
        const until = sessionStorage.getItem(`nx-crit-snooze-${v.chassiVin}`);
        if (until && Number(until) > agora) snoozed = true;
      } catch {
        /* ignore */
      }
      if (snoozed) continue;
      const posicao = posicaoDoVeiculo(v, cfg);
      criticalDispatchedRef.current.add(v.chassiVin);
      setCriticalTarget({
        vin: v.chassiVin,
        prefix: v.localPrefix,
        socPct: soc,
        /* Autonomia pode ser IMPOSSÍVEL nesta fonte: `valorUtilizavel` devolve
           `null` e o alerta não inventa um número de quilômetros. */
        rangeKm: numeroUtilizavel(v, "range_km", cfg),
        lat: posicao?.[0] ?? null,
        lon: posicao?.[1] ?? null,
      });
      break;
    }
  }, [veiculos, cfg, criticalTarget]);

  const handleCloseCritical = () => {
    criticalDispatchedRef.current.clear();
    setCriticalTarget(null);
  };
  const navigateToMap = (vin: string) => {
    navigate(`/app/mapa_frota?focus=${encodeURIComponent(vin)}`);
  };

  if (error && !data) {
    return (
      <div className="nx-card">
        {/* FRASE, e nao `Error: HTTP 403`. O `toString()` de uma excecao nao
            e texto para operador. Ver `lib/falha-de-carga.ts`. */}
        <div className="nx-error">{fraseDeFalhaDeCarga(error, "a telemetria da frota")}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <div className="nx-page-head">
          <div className="nx-page-head__meta">
            <span className="nx-page-head__eyebrow">Frota Elétrica</span>
            <h1>Dados dos Ônibus</h1>
            <p className="nx-page-head__sub">Carregando snapshot da frota…</p>
          </div>
        </div>
        <div className="nx-kpi-grid" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={`s-${i}`} variant="kpi" />
          ))}
        </div>
        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <Skeleton variant="row" count={8} />
        </div>
      </div>
    );
  }

  /* TODOS os KPIs derivam DESTE array — o mesmo que a tabela renderiza. */
  const contagem = contagemDaFrota(veiculos, cfg);
  const movimento = contagemDeMovimento(veiculos, cfg);
  const semSinal = semSinalDaFrota(contagem);
  const socMedio = mediaDoCampo(veiculos, "soc_pct", cfg);
  const socMin = minimoDoCampo(veiculos, "soc_pct", cfg);
  const socMax = maximoDoCampo(veiculos, "soc_pct", cfg);

  /* Colunas visíveis: a que nenhuma fonte exibida fornece SOME. */
  const colunas = COLUNAS.filter(
    (c) => c.tipo !== "campo" || !campoOculto(veiculos, c.campo),
  );
  const camposDaTabela = COLUNAS.flatMap((c) => (c.tipo === "campo" ? [c.campo] : []));
  const nota = notaDeCamposOcultos(veiculos, camposDaTabela);

  /* Campos declarados impossíveis que chegaram COM valor — bug de dado.
     `contagemDoCampo` em vez de um `.some()` escrito à mão: é o mesmo
     classificador, e devolve a contagem por estado de uma vez só. */
  const inconsistentes = camposDaTabela.filter(
    (campo) => contagemDoCampo(veiculos, campo, cfg)["inconsistencia-de-fonte"] > 0,
  );
  const fontes = [...new Set(veiculos.map((v) => v.sourceLabel))].sort();

  /* Contradicoes DENTRO de leituras correntes. `null` quando nao ha — banner
     permanente em zero treina o operador a ignorar banners. */
  const fraseDeContradicao = fraseDeContradicaoDeLeitura(contradicoesDeLeitura(veiculos, cfg));

  /*
    O TRUNCAMENTO, DECLARADO.

    O envelope publica `totalVisivel` (a topologia de `fleet_snapshot`) ao lado
    de `amostrados` (quantas linhas vieram sob o `limit`), e existem justamente
    para o recorte ser DIZÍVEL: com o limite atingido a tabela renderiza N linhas
    e os KPIs, derivados do mesmo array, falam de N — mas o cabeçalho anuncia
    "ônibus visíveis", que é uma afirmação sobre a topologia inteira. Sem a
    ressalva, os dois números divergem e nada na tela permite saber qual deles é
    a frota.

    `null` quando não há truncamento: uma ressalva permanente sobre um recorte
    que não existe treina o operador a ignorá-la.
  */
  const truncamento =
    data === null ? null : ressalvaDeTruncamento(data.amostrados, data.totalVisivel);

  const filtrados = veiculos.filter((v) => {
    if (filter.trim()) {
      const q = filter.trim().toLowerCase();
      const hit =
        (v.localPrefix ?? "").toLowerCase().includes(q) ||
        v.chassiVin.toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (statusFilter !== "") return estadoDeMovimento(v, cfg) === statusFilter;
    return true;
  });

  if (sortKey !== null) {
    const dir = sortDir === "asc" ? 1 : -1;
    /* Nulos sempre no fim, independente da direção: o operador nunca deve ver a
       coluna de travessões no topo. */
    const cmpNum = (a: number | null, b: number | null): number => {
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      return (a - b) * dir;
    };
    const cmpStr = (a: string | null, b: string | null): number => {
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b, "pt-BR") * dir;
    };
    filtrados.sort((a, b) => {
      switch (sortKey) {
        case "prefix":
          return cmpStr(a.localPrefix, b.localPrefix);
        case "chassi":
          return cmpStr(a.chassiVin, b.chassiVin);
        case "estado":
          return (
            (ORDEM_DO_MOVIMENTO[estadoDeMovimento(a, cfg)] -
              ORDEM_DO_MOVIMENTO[estadoDeMovimento(b, cfg)]) *
            dir
          );
        case "gps":
          /* Ordena por latitude (proxy geográfico N-S) usando a posição
             VALIDADA: veículo com `(0,0)` ou carimbo futuro não sobe ao topo
             como se estivesse no extremo sul. */
          return cmpNum(posicaoDoVeiculo(a, cfg)?.[0] ?? null, posicaoDoVeiculo(b, cfg)?.[0] ?? null);
        case "updated":
          /* Pela IDADE, não pelo texto do carimbo: ordenar string de hora
             misturava dias, e o veículo sem leitura não tem carimbo para
             comparar. O COMPARADOR vem do módulo — a tela não lê `ageSeconds`,
             porque ler o campo é o que permite o alias que derrota a guarda. */
          return compararPorIdade(a, b, dir);
        default:
          return cmpNum(numeroUtilizavel(a, sortKey, cfg), numeroUtilizavel(b, sortKey, cfg));
      }
    });
  }

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__meta">
          <span className="nx-page-head__eyebrow">Frota Elétrica</span>
          <h1>Dados dos Ônibus</h1>
          <p className="nx-page-head__sub">
            {fontes.length > 0 ? fontes.join(" · ") : "Sem fonte exibida"} ·{" "}
            {/* NONA ocorrência: era `{contagem.total} ônibus visíveis`, e com
                `total === 1` saía "1 ônibus visíveis". "visíveis" é ADJETIVO, e
                por isso nenhuma denylist de verbo o alcançava. */}
            {fraseDeOnibusVisiveis(contagem.total)} ·{" "}
            {/* O DESCARTE É DECLARADO. Linha sem identidade não vira veículo, e
                uma frota que encolhe em silêncio é o defeito de denominador com
                outra causa — "o servidor mandou lixo" não autoriza a tela a
                mentir sobre o total. */}
            {descartadas > 0 && (
              <strong style={{ color: "var(--nx-alert-ink)" }}>
                {/* Eram dois ternários inline acertando número em duas palavras
                    que têm de concordar entre si. Funcionava; a terceira cópia
                    da regra é que erra. A frase mora na tabela agora. */}
                {fraseDeLinhasDescartadas(descartadas)} ·{" "}
              </strong>
            )}
            atualizado{" "}
            {now.toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </p>
        </div>
        <div
          className="nx-page-head__actions"
          style={{ display: "flex", gap: 12, alignItems: "center" }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: ".85rem",
              color: "var(--nx-mute)",
              cursor: "pointer",
            }}
            title="Recarrega automaticamente a lista a cada 30s"
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto refresh (30s)
          </label>
        </div>
      </div>

      {/*
        OS QUATRO KPIs, todos derivados do mesmo array da tabela.

        "Ativos agora" virou "Com sinal" e ganhou dois irmãos que antes não
        existiam em contagem nenhuma: "Sem sinal" (vencido + nunca reportou) e o
        carimbo à frente, que aparece só quando há — um card permanente em zero
        para uma falha rara treina o operador a ignorar o card.
      */}
      {/*
        O PORTAL PAROU DE BUSCAR — e isto NAO e "a fonte parou".

        `role="alert"` e nao `status`: e falha, e ela invalida a leitura de TODA a
        tela abaixo. Enquanto ela esta no ar, os badges "Rodando" e os KPIs de
        leitura corrente continuam afirmando o AGORA sobre uma resposta que
        envelhece sem que nada na tela mude.

        Fica ACIMA dos KPIs de proposito: e a ressalva que governa todos eles.
      */}
      {falhaDeAtualizacao !== null && (
        <p
          role="alert"
          style={{
            margin: "0 0 var(--nx-space-4)",
            padding: ".55rem .75rem",
            borderLeft: "3px solid var(--nx-alert-ink)",
            background: "var(--nx-alert-bg)",
            color: "var(--nx-alert-ink)",
            fontSize: ".82rem",
            borderRadius: "var(--nx-radius-xs)",
          }}
        >
          {fraseDeAtualizacaoParada(
            falhaDeAtualizacao.tentativas,
            falhaDeAtualizacao.ultimoSucesso === null
              ? null
              : /* Contra `falhouEm`, e NAO contra `now`: `now` congela no ultimo
                   sucesso, entao a conta dava sempre 0 s. Ver `falhouEm`. */
                fmtAge(
                  (falhaDeAtualizacao.falhouEm - falhaDeAtualizacao.ultimoSucesso) / 1000,
                ),
          )}
        </p>
      )}

      <div className="nx-kpi-grid" role="status" aria-live="polite" aria-atomic="false">
        <KpiDeFrota
          label="Ônibus visíveis"
          valor={String(contagem.total)}
          hint="topologia da fonte ativa — é o denominador de todos os indicadores"
          tone="navy"
        />
        <KpiDeFrota
          label="Com sinal"
          /* `valorDeParcela` e nao `String(...)`: com a frota visivel vazia,
             "COM SINAL 0" afirma algo sobre onibus que nao existem. Ver
             `valorDeParcela`. */
          valor={valorDeParcela(contagem.correntes, contagem.total)}
          hint={`leitura nos últimos ${fmtAge(cfg.staleAfterSeconds)}`}
          tone="success"
        />
        {/* Número E decomposição da MESMA função: a dica enumerava só dois dos
            três baldes e dizia "1 nunca reportaram". */}
        <KpiDeFrota
          label="Sem sinal"
          /* `semSinal.valor` e nao `String(semSinal.total)`: com a frota
             visivel VAZIA o KPI imprime travessao, como os tres vizinhos, em
             vez de "0" ao lado de uma dica que afirmava leitura corrente
             sobre zero veiculos. Ver `semSinalDaFrota`. */
          valor={semSinal.valor}
          hint={semSinal.frase}
          tone="orange"
        />
        <KpiDeFrota
          label="Carregando"
          valor={valorDeParcela(movimento.carregando, contagem.total)}
          hint="plugue conectado em leitura corrente — exclui quem está sem sinal"
          tone="cyan"
        />
      </div>

      {contagem.futuros > 0 && (
        <p
          role="alert"
          style={{
            margin: "var(--nx-space-3) 0 0",
            padding: ".55rem .75rem",
            borderLeft: "3px solid var(--nx-state-clock-ink)",
            /* Par SÓLIDO do estado `carimbo-futuro` — o MESMO do badge e do pin,
               e é o ponto: a mesma causa não pode ter duas cores em três dobras.
               Era `--nx-danger` (não vira com o tema: 3,95:1 sobre o fundo
               escuro) sobre `color-mix(... 8%, transparent)`, e o fundo
               translúcido deixava a razão à mercê do que estivesse atrás. */
            background: "var(--nx-state-clock-bg)",
            color: "var(--nx-state-clock-ink)",
            fontSize: ".82rem",
            borderRadius: "var(--nx-radius-xs)",
          }}
        >
          {/* A FRASE VEM PRONTA e concorda em NUMERO. Era JSX inline com "tem"
              plural e "desses veiculos" cravados, e com `futuros === 1` a tela
              dizia "1 de 18 onibus tem carimbo... nenhuma leitura desses
              veiculos". O invariante de genero nao alcancava isto porque varre o
              produto de duas TABELAS, e isto era texto inline. Ver
              `CONCORDANCIA_DE_NUMERO`. */}
          {fraseDeCarimboFuturoNaTabela(contagem.futuros, contagem.total)}
        </p>
      )}

      {/*
        CONTRADICAO DENTRO DE UMA LEITURA CORRENTE — a celula que faltava.

        Onibus a 31 km/h com `plug_charging: true` resolvia para "Rodando" e o
        plugue era DESCARTADO em silencio. A precedencia esta certa; o silencio
        nao estava. `inconsistencia-de-fonte` foi construido para o caso
        estruturalmente identico — duas afirmacoes incompativeis sobre o mesmo
        dado — e nao havia nada para "dois campos da MESMA leitura se
        contradizem". Ver `contradicoesDeLeitura`.
      */}
      {fraseDeContradicao !== null && (
        <p
          role="alert"
          style={{
            margin: "var(--nx-space-3) 0 0",
            padding: ".55rem .75rem",
            borderLeft: "3px solid var(--nx-alert-ink)",
            background: "var(--nx-alert-bg)",
            color: "var(--nx-alert-ink)",
            fontSize: ".82rem",
            borderRadius: "var(--nx-radius-xs)",
          }}
        >
          {fraseDeContradicao}
        </p>
      )}

      {/*
        SOC agregado. Os três números saem de `mediaDoCampo`/`minimoDoCampo`/
        `maximoDoCampo`, que declaram "M de N" — antes eram `AVG/MIN/MAX` sem
        denominador nenhum sobre uma relação diferente da que alimentava a
        tabela.
      */}
      <div
        className="nx-kpi-grid"
        style={{ marginTop: "var(--nx-space-4)", marginBottom: "var(--nx-space-6)" }}
      >
        <KpiAgregado
          label="SOC médio"
          agregado={socMedio}
          formatar={(v) => `${num1(v)}%`}
          tone="navy"
        />
        <KpiAgregado
          label="SOC mínimo"
          agregado={socMin}
          formatar={(v) => `${num1(v)}%`}
          tone="navy"
        />
        <KpiAgregado
          label="SOC máximo"
          agregado={socMax}
          formatar={(v) => `${num1(v)}%`}
          tone="navy"
        />
      </div>

      <div className="nx-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "var(--nx-space-3)",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ fontSize: "var(--nx-text-md)", margin: 0 }}>Snapshot por veículo</h2>
          <div
            style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}
          >
            <label
              htmlFor="tf-filter"
              style={{ fontSize: ".75rem", color: "var(--nx-mute)", whiteSpace: "nowrap" }}
            >
              Prefixo:
            </label>
            <input
              id="tf-filter"
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="ex: 82001"
              className="nx-input"
              style={{ width: "9rem", fontFamily: "var(--nx-font-mono, monospace)" }}
              autoComplete="off"
            />
            <label
              htmlFor="tf-status"
              style={{
                fontSize: ".75rem",
                color: "var(--nx-mute)",
                whiteSpace: "nowrap",
                marginLeft: ".5rem",
              }}
            >
              Estado:
            </label>
            <select
              id="tf-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "" | EstadoDeMovimento)}
              className="nx-input"
              style={{ width: "11rem" }}
            >
              <option value="">Todos</option>
              <option value="rodando">Rodando ({movimento.rodando})</option>
              <option value="carregando">Carregando ({movimento.carregando})</option>
              <option value="parado">Parado ({movimento.parado})</option>
              <option value="indeterminado">
                Indeterminado ({movimento.indeterminado})
              </option>
              <option value="sem-sinal">Sem sinal ({movimento["sem-sinal"]})</option>
              <option value="sem-leitura">
                Nunca reportou ({movimento["sem-leitura"]})
              </option>
              <option value="carimbo-futuro">
                Carimbo à frente ({movimento["carimbo-futuro"]})
              </option>
            </select>
            {(filter || statusFilter) && (
              <button
                type="button"
                onClick={() => {
                  setFilter("");
                  setStatusFilter("");
                }}
                className="nx-btn nx-btn--ghost"
                style={{ fontSize: ".75rem", padding: ".3rem .6rem" }}
                aria-label="Limpar filtros"
              >
                Limpar
              </button>
            )}
          </div>
        </div>

        <AvisoDeInconsistencia campos={inconsistentes} fonte={fontes.join(" e ")} />
        {truncamento !== null && <NotaDeFonte>{truncamento}</NotaDeFonte>}
        {nota !== null && <NotaDeFonte>{nota}</NotaDeFonte>}

        {veiculos.length === 0 ? (
          <p style={{ color: "var(--nx-mute)", margin: 0 }}>
            Nenhum ônibus visível para a fonte de telemetria ativa. Se isto for
            inesperado, confira se o serviço de sincronia está classificando a
            procedência dos veículos.
          </p>
        ) : (
          /*
            A REGIAO ROLAVEL E O NOME ACESSIVEL DA TABELA.

            `.nx-table-wrap` e rolagem HORIZONTAL de verdade, e uma regiao que
            rola sem foco de teclado nao se alcanca sem mouse (WCAG 2.1.1). Com
            `tabIndex={0}` ela entra na ordem de tabulacao e as setas rolam; com
            `role="region"` + `aria-label` o leitor de tela anuncia o que e a
            regiao em que o usuario acabou de entrar, em vez de um grupo mudo.

            E a TABELA nao tinha nome nenhum: sem `<caption>` e sem `aria-label`,
            o leitor anunciava "tabela, 9 colunas" e nada sobre o que ela conta.
            O `<caption>` e visualmente escondido (`nx-sr-only`) porque o titulo
            ja esta na tela acima — duplicar o texto visivel seria ruido para
            quem enxerga, e a ausencia dele era ruido para quem nao enxerga.

            O nome DECLARA o denominador, que e o produto desta fase: quem ouve
            "18 onibus" sabe sobre quantos as linhas falam antes de percorre-las.
          */
          <div
            className="nx-table-wrap"
            role="region"
            tabIndex={0}
            aria-label={`Tabela de telemetria da frota — ${fraseDeOnibusVisiveis(contagem.total)}`}
          >
            <table className="nx-table">
              <caption className="nx-sr-only">
                {`Telemetria por ônibus. ${fraseDeOnibusVisiveis(contagem.total)}. `}
                {/* ACENTO, e nao e cosmetico: "e" por "é" troca o VERBO pela
    conjunção na leitura, e este texto SÓ existe para o leitor de tela —
    ninguém o vê para notar. É a mesma razão pela qual o `aria-label`
    agramatical é pior que o visível. */}
                {`Colunas ordenáveis; o cabeçalho de cada coluna é um botão.`}
              </caption>
              <thead>
                <tr>
                  {colunas.map((coluna) => {
                    const key = chaveDaColuna(coluna);
                    const ativo = sortKey === key;
                    return (
                      <th
                        key={key}
                        /* `scope="col"` nos nove cabecalhos: sem ele o leitor de
                           tela nao associa celula a coluna, e a tabela vira uma
                           lista de numeros soltos. */
                        scope="col"
                        className={coluna.tipo === "campo" && coluna.num ? "num" : undefined}
                        aria-sort={
                          ativo
                            ? sortDir === "asc"
                              ? "ascending"
                              : "descending"
                            : "none"
                        }
                        style={{ padding: 0 }}
                      >
                        {/*
                          `<button>` dentro do `<th>` em vez de `onClick` no
                          `<th>`: o cabeçalho clicável não era alcançável por
                          teclado nem anunciado como controle. Agora tem foco,
                          Enter/Espaço e `aria-sort` no `<th>`.
                        */}
                        <button
                          type="button"
                          onClick={() => toggleSort(key)}
                          style={{
                            all: "unset",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            width: "100%",
                            padding: ".5rem .6rem",
                            cursor: "pointer",
                            justifyContent:
                              coluna.tipo === "campo" && coluna.num ? "flex-end" : "flex-start",
                          }}
                        >
                          {coluna.label}
                          <span
                            aria-hidden="true"
                            style={{
                              fontSize: ".8em",
                              /* `--nx-orange-ink` e não `--nx-orange-500`: o
                                 laranja de preenchimento dá 3,08:1 sobre o fundo
                                 claro. O indicador é `aria-hidden`, mas quem
                                 enxerga precisa enxergá-lo. */
                              color: ativo ? "var(--nx-orange-ink)" : "var(--nx-mute)",
                            }}
                          >
                            {sortIndicator(key)}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filtrados.length === 0 ? (
                  <tr>
                    <td
                      colSpan={colunas.length}
                      style={{
                        textAlign: "center",
                        color: "var(--nx-mute)",
                        padding: "1.5rem",
                      }}
                    >
                      Nenhum ônibus bate o filtro atual.
                    </td>
                  </tr>
                ) : (
                  filtrados.map((v) => (
                    <LinhaDeVeiculo
                      key={v.chassiVin}
                      veiculo={v}
                      colunas={colunas}
                      cfg={cfg}
                      onOpen={setSelectedVin}
                      onNavigateToMap={navigateToMap}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selectedVin && (
        <VehicleDetailModal vin={selectedVin} onClose={() => setSelectedVin(null)} />
      )}
      {criticalTarget && (
        <CriticalBatteryAlert
          vehicle={criticalTarget}
          onClose={handleCloseCritical}
          onNavigateToMap={(vin) => {
            handleCloseCritical();
            navigateToMap(vin);
          }}
        />
      )}
    </div>
  );
}

/**
 * Uma linha.
 *
 * A DEGRADAÇÃO É POR CÉLULA, não pela linha inteira, e a mudança é deliberada:
 * antes um `staleStyle` na `<tr>` acinzentava tudo, inclusive o prefixo e o
 * chassi — que são CADASTRO e não mudam com o sinal. Acinzentar identidade
 * sugere que o veículo é incerto, e o incerto é a leitura. Cada célula carrega
 * o tratamento do SEU estado, que é o que `TRATAMENTO_DO_CAMPO` decide.
 */
function LinhaDeVeiculo({
  veiculo,
  colunas,
  cfg,
  onOpen,
  onNavigateToMap,
}: {
  veiculo: VeiculoDeFrota;
  colunas: readonly Coluna[];
  cfg: ReturnType<typeof configuracaoDeFrota>;
  onOpen: (vin: string) => void;
  onNavigateToMap: (vin: string) => void;
}) {
  const movimento = estadoDeMovimento(veiculo, cfg);
  const idade = textoDaIdade(veiculo, cfg, fmtAge);
  const posicao = posicaoDoVeiculo(veiculo, cfg);
  const gpsFrase = celulaDeCampo(veiculo, "gps_lat", cfg, fmtAge).frase;

  return (
    <tr>
      {colunas.map((coluna) => {
        if (coluna.tipo === "campo") {
          return (
            <CampoCelula
              key={coluna.campo}
              className="num mono"
              celula={celulaDeCampo(veiculo, coluna.campo, cfg, fmtAge)}
              formatar={coluna.formatar}
            />
          );
        }
        if (coluna.key === "prefix") {
          return (
            <td key="prefix" className="id mono" style={{ fontWeight: 700 }}>
              <button
                type="button"
                onClick={() => onOpen(veiculo.chassiVin)}
                title="Ver detalhes do ônibus"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "var(--nx-link)",
                  font: "inherit",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {rotuloDoVeiculo(veiculo)}
              </button>
            </td>
          );
        }
        if (coluna.key === "chassi") {
          return (
            <td key="chassi" className="mono" style={{ fontSize: ".75rem" }}>
              {veiculo.chassiVin}
            </td>
          );
        }
        if (coluna.key === "estado") {
          return (
            <td key="estado">
              <BadgeDeMovimento
                estado={movimento}
                frase={FRASE_DO_MOVIMENTO[movimento](veiculo.sourceLabel)}
              />
            </td>
          );
        }
        if (coluna.key === "gps") {
          return (
            <td key="gps" className="mono" style={{ fontSize: ".72rem" }}>
              <GpsCell
                posicao={posicao}
                frase={gpsFrase}
                vin={veiculo.chassiVin}
                onNavigate={onNavigateToMap}
              />
            </td>
          );
        }
        return (
          <td key="updated" style={{ fontSize: ".75rem", color: "var(--nx-mute)" }}>
            {idade}
          </td>
        );
      })}
    </tr>
  );
}
